<script lang="ts">
  import { tick } from 'svelte';
  import { link, push } from 'svelte-spa-router';
  import { searchQuery, searchResults, isSearching, debouncedSearch } from '$stores/search.js';
  import { fetchPreview } from '$lib/linkPreview.js';
  import type { PreviewContent } from '$stores/preview.js';
  import LinkPreview from './LinkPreview.svelte';

  let inputValue = '';
  let showPreview = false;
  let inputElement: HTMLInputElement;
  let mobileInputElement: HTMLInputElement;

  // Mobile overlay state
  let mobileExpanded = false;

  // Article preview on hover
  let hoverPreview: PreviewContent | null = null;
  let hoverLinkRect: DOMRect | null = null;
  let hoverDropdownRect: DOMRect | null = null;
  let hoverTimeout: ReturnType<typeof setTimeout> | null = null;
  let hidePreviewTimeout: ReturnType<typeof setTimeout> | null = null;
  let previewVisible = false;
  let dropdownElement: HTMLDivElement;

  async function expandMobile() {
    mobileExpanded = true;
    await tick();
    mobileInputElement?.focus();
  }

  function collapseMobile() {
    mobileExpanded = false;
    inputValue = '';
    showPreview = false;
    dismissHoverPreview();
    debouncedSearch('');
  }

  function handleInput() {
    debouncedSearch(inputValue);
    showPreview = inputValue.length > 0;
    dismissHoverPreview();
  }

  function handleFocus() {
    if (inputValue.length > 0) {
      showPreview = true;
    }
  }

  function handleBlur() {
    // Delay hiding to allow click on results
    setTimeout(() => {
      showPreview = false;
      dismissHoverPreview();
    }, 200);
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && inputValue.trim()) {
      showPreview = false;
      dismissHoverPreview();
      if (mobileExpanded) collapseMobile();
      push(`/search?q=${encodeURIComponent(inputValue)}`);
    }
    if (event.key === 'Escape') {
      showPreview = false;
      dismissHoverPreview();
      if (mobileExpanded) {
        collapseMobile();
      } else {
        inputElement?.blur();
      }
    }
  }

  function goToResult(path: string) {
    showPreview = false;
    inputValue = '';
    dismissHoverPreview();
    if (mobileExpanded) collapseMobile();
    push(`/${path}`);
  }

  function dismissHoverPreview() {
    if (hoverTimeout) { clearTimeout(hoverTimeout); hoverTimeout = null; }
    if (hidePreviewTimeout) { clearTimeout(hidePreviewTimeout); hidePreviewTimeout = null; }
    previewVisible = false;
    hoverPreview = null;
    hoverLinkRect = null;
    hoverDropdownRect = null;
  }

  function handleResultMouseEnter(event: MouseEvent, resultPath: string) {
    if (hidePreviewTimeout) { clearTimeout(hidePreviewTimeout); hidePreviewTimeout = null; }
    if (hoverTimeout) { clearTimeout(hoverTimeout); hoverTimeout = null; }

    const target = (event.currentTarget as HTMLElement);

    hoverTimeout = setTimeout(async () => {
      const preview = await fetchPreview(resultPath);
      if (!preview) return;

      hoverPreview = preview;
      hoverLinkRect = target.getBoundingClientRect();
      hoverDropdownRect = dropdownElement ? dropdownElement.getBoundingClientRect() : null;
      previewVisible = true;
    }, 250);
  }

  function handleResultMouseLeave() {
    if (hoverTimeout) { clearTimeout(hoverTimeout); hoverTimeout = null; }

    hidePreviewTimeout = setTimeout(() => {
      previewVisible = false;
      hoverPreview = null;
      hoverLinkRect = null;
      hoverDropdownRect = null;
    }, 150);
  }

  function handlePreviewMouseEnter() {
    if (hidePreviewTimeout) { clearTimeout(hidePreviewTimeout); hidePreviewTimeout = null; }
  }

  function handlePreviewMouseLeave() {
    hidePreviewTimeout = setTimeout(() => {
      previewVisible = false;
      hoverPreview = null;
      hoverLinkRect = null;
      hoverDropdownRect = null;
    }, 150);
  }
</script>

<!-- Mobile: collapsed icon trigger -->
<button class="mobile-search-btn" on:click={expandMobile} aria-label={__STRINGS__.search.buttonLabel}>
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <circle cx="11" cy="11" r="8"/>
    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
</button>

<!-- Mobile: expanded overlay -->
{#if mobileExpanded}
  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
  <div class="mobile-overlay" on:click|self={collapseMobile}>
    <div class="mobile-overlay-bar">
      <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input
        bind:this={mobileInputElement}
        bind:value={inputValue}
        on:input={handleInput}
        on:focus={handleFocus}
        on:blur={handleBlur}
        on:keydown={handleKeydown}
        type="search"
        placeholder={__STRINGS__.search.placeholder}
        aria-label={__STRINGS__.search.buttonLabel}
      />
      {#if $isSearching}
        <div class="spinner"></div>
      {/if}
      <button class="close-btn" on:click={collapseMobile} aria-label={__STRINGS__.search.closeLabel}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>

    {#if showPreview && $searchResults.length > 0}
      <div class="search-preview mobile-search-preview">
        {#each $searchResults.slice(0, 5) as result}
          <button
            class="preview-item"
            on:click={() => goToResult(result.path)}
          >
            <span class="preview-type" class:wiki={result.type === 'wiki'}>
              {result.type === 'wiki' ? __STRINGS__.search.tagWiki : __STRINGS__.search.tagVolume}
            </span>
            <div class="preview-content">
              <span class="preview-title">{result.title}</span>
              <span class="preview-excerpt">{result.excerpt}</span>
            </div>
          </button>
        {/each}
        {#if $searchResults.length > 5}
          <a href="/search?q={encodeURIComponent(inputValue)}" use:link class="view-all" on:click={collapseMobile}>
            {__STRINGS__.search.viewAll.replace('{count}', String($searchResults.length))}
          </a>
        {/if}
      </div>
    {:else if showPreview && inputValue.length > 0 && !$isSearching}
      <div class="search-preview mobile-search-preview">
        <div class="no-results">{__STRINGS__.search.noResults.replace('{query}', inputValue)}</div>
      </div>
    {/if}
  </div>
{/if}

<!-- Desktop: normal search bar -->
<div class="search-bar">
  <div class="search-input-wrapper">
    <svg class="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
    <input
      bind:this={inputElement}
      bind:value={inputValue}
      on:input={handleInput}
      on:focus={handleFocus}
      on:blur={handleBlur}
      on:keydown={handleKeydown}
      type="search"
      placeholder={__STRINGS__.search.placeholder}
      aria-label={__STRINGS__.search.buttonLabel}
    />
    {#if $isSearching}
      <div class="spinner"></div>
    {/if}
  </div>

  {#if showPreview && $searchResults.length > 0}
    <div class="search-preview" bind:this={dropdownElement}>
      {#each $searchResults.slice(0, 5) as result}
        <!-- svelte-ignore a11y_mouse_events_have_key_events -->
        <button
          class="preview-item"
          on:click={() => goToResult(result.path)}
          on:mouseenter={(e) => handleResultMouseEnter(e, result.path)}
          on:mouseleave={handleResultMouseLeave}
        >
          <span class="preview-type" class:wiki={result.type === 'wiki'}>
            {result.type === 'wiki' ? 'Wiki' : 'Vol'}
          </span>
          <div class="preview-content">
            <span class="preview-title">{result.title}</span>
            <span class="preview-excerpt">{result.excerpt}</span>
          </div>
        </button>
      {/each}
      {#if $searchResults.length > 5}
        <a href="/search?q={encodeURIComponent(inputValue)}" use:link class="view-all">
          {__STRINGS__.search.viewAll.replace('{count}', String($searchResults.length))}
        </a>
      {/if}
    </div>
  {:else if showPreview && inputValue.length > 0 && !$isSearching}
    <div class="search-preview">
      <div class="no-results">No results found</div>
    </div>
  {/if}
</div>

{#if previewVisible && hoverPreview && hoverLinkRect}
  {#key hoverPreview.path}
    <LinkPreview
      preview={hoverPreview}
      linkRect={hoverLinkRect}
      parentRect={hoverDropdownRect}
      level={1}
      onMouseEnter={() => handlePreviewMouseEnter()}
      onMouseLeave={() => handlePreviewMouseLeave()}
      onLinkHover={null}
      onClickLink={null}
    />
  {/key}
{/if}

<style>
  /* ── Desktop search bar ────────────────────────────────────── */
  .search-bar {
    position: relative;
    width: 280px;
  }

  .search-input-wrapper {
    display: flex;
    align-items: center;
    background: var(--color-menubar-hover, rgba(255, 255, 255, 0.1));
    border-radius: 6px;
    padding: 0 0.75rem;
  }

  .search-icon {
    color: var(--color-menubar-text, #fff);
    opacity: 0.6;
    flex-shrink: 0;
  }

  input {
    flex: 1;
    background: none;
    border: none;
    padding: 0.5rem;
    color: var(--color-menubar-text, #fff);
    font-size: 0.9rem;
    outline: none;
  }

  input::placeholder {
    color: var(--color-menubar-text, #fff);
    opacity: 0.5;
  }

  .spinner {
    width: 14px;
    height: 14px;
    border: 2px solid var(--color-menubar-text, #fff);
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
    flex-shrink: 0;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .search-preview {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin-top: 0.5rem;
    background: var(--color-surface, #fff);
    border: 1px solid var(--color-border, #e0e0e0);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 200;
    overflow: hidden;
  }

  .preview-item {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    width: 100%;
    padding: 0.75rem;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s;
  }

  .preview-item:hover {
    background: var(--color-surface-hover, #f5f5f5);
  }

  .preview-item + .preview-item {
    border-top: 1px solid var(--color-border, #e0e0e0);
  }

  .preview-type {
    flex-shrink: 0;
    padding: 0.15rem 0.4rem;
    background: var(--color-primary, #e94560);
    color: white;
    font-size: 0.65rem;
    font-weight: 600;
    text-transform: uppercase;
    border-radius: 3px;
  }

  .preview-type.wiki {
    background: var(--color-link, #0f3460);
  }

  .preview-content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .preview-title {
    font-weight: 500;
    color: var(--color-text, #333);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .preview-excerpt {
    font-size: 0.8rem;
    color: var(--color-text-muted, #666);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .view-all {
    display: block;
    padding: 0.75rem;
    text-align: center;
    color: var(--color-link, #0f3460);
    text-decoration: none;
    border-top: 1px solid var(--color-border, #e0e0e0);
    font-size: 0.85rem;
  }

  .view-all:hover {
    background: var(--color-surface-hover, #f5f5f5);
  }

  .no-results {
    padding: 1rem;
    text-align: center;
    color: var(--color-text-muted, #666);
    font-size: 0.9rem;
  }

  /* ── Mobile trigger button ─────────────────────────────────── */
  .mobile-search-btn {
    display: none;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    background: var(--color-menubar-hover, rgba(255, 255, 255, 0.1));
    border: none;
    border-radius: 6px;
    color: var(--color-menubar-text, #fff);
    cursor: pointer;
  }

  /* ── Mobile expanded overlay ───────────────────────────────── */
  .mobile-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 150;
    /* Transparent backdrop — tap outside bar to close */
    background: transparent;
  }

  .mobile-overlay-bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    height: 56px;
    padding: 0 0.75rem;
    background: var(--color-menubar-bg, #1a1a2e);
    /* Prevent backdrop tap-to-close from firing on the bar itself */
    pointer-events: all;
  }

  .mobile-overlay-bar .search-icon {
    flex-shrink: 0;
  }

  .mobile-overlay-bar input {
    flex: 1;
    background: none;
    border: none;
    padding: 0.5rem 0;
    color: var(--color-menubar-text, #fff);
    font-size: 1rem;
    outline: none;
    min-width: 0;
  }

  .mobile-overlay-bar input::placeholder {
    color: var(--color-menubar-text, #fff);
    opacity: 0.5;
  }

  .close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    background: none;
    border: none;
    color: var(--color-menubar-text, #fff);
    cursor: pointer;
    opacity: 0.8;
    flex-shrink: 0;
    border-radius: 4px;
  }

  .close-btn:hover {
    opacity: 1;
    background: var(--color-menubar-hover, rgba(255, 255, 255, 0.1));
  }

  .mobile-search-preview {
    position: absolute;
    top: 56px;
    left: 0;
    right: 0;
    background: var(--color-surface, #fff);
    border-bottom: 1px solid var(--color-border, #e0e0e0);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 151;
    overflow: hidden;
  }

  /* ── Responsive visibility ─────────────────────────────────── */
  @media (max-width: 768px) {
    .search-bar {
      display: none;
    }

    .mobile-search-btn {
      display: flex;
    }
  }
</style>
